// Performance optimization configuration
export const performanceConfig = {
  // Image optimization
  images: {
    lazyLoading: true,
    preloadHero: true,
    quality: 80,
    formats: ['webp', 'jpg'],
    sizes: {
      thumbnail: '150px',
      card: '300px',
      hero: '800px'
    }
  },

  // Animation optimization
  animations: {
    reducedMotion: true,
    maxDuration: 500,
    useTransform: true,
    willChange: true
  },

  // Component optimization
  components: {
    memoization: true,
    virtualization: true,
    debounceDelay: 300,
    throttleDelay: 100
  },

  // Bundle optimization
  bundle: {
    codeSplitting: true,
    lazyLoading: true,
    treeShaking: true,
    minification: true
  },

  // API optimization
  api: {
    caching: true,
    debouncing: true,
    pagination: true,
    requestLimit: 10
  }
};

// Performance monitoring
export const performanceMetrics = {
  // Core Web Vitals thresholds
  coreWebVitals: {
    lcp: 2500, // Largest Contentful Paint (ms)
    fid: 100,  // First Input Delay (ms)
    cls: 0.1   // Cumulative Layout Shift
  },

  // Custom metrics
  custom: {
    timeToInteractive: 3500,
    firstMeaningfulPaint: 2000,
    domContentLoaded: 1500
  }
};

// Performance utilities
export const performanceUtils = {
  // Debounce function
  debounce: <T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  },

  // Throttle function
  throttle: <T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): ((...args: Parameters<T>) => void) => {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // Intersection Observer for lazy loading
  createIntersectionObserver: (
    callback: IntersectionObserverCallback,
    options: IntersectionObserverInit = {}
  ) => {
    const defaultOptions: IntersectionObserverInit = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1,
      ...options
    };
    
    return new IntersectionObserver(callback, defaultOptions);
  },

  // Performance measurement
  measurePerformance: (name: string, fn: () => void) => {
    const start = performance.now();
    fn();
    const end = performance.now();
    console.log(`${name} took ${end - start} milliseconds`);
  }
};

export default performanceConfig;
